import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMatch } from "@/hooks/useMatch";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Card, Btn, Field } from "@/components/admin/MatchSections";
import { DAYS, fmtDate } from "@/lib/football";

const MotmPanel = ({ id }) => {
  const { data, act } = useMatch(id);
  const [multi, setMulti] = useState([]);
  if (!data) return null;
  const m = data.match;
  const players = data.players.filter((p) => p.registration_status === "confirmed" && p.team);
  const ranked = [...players].sort((a, b) => (data.tally[b.id] || 0) - (data.tally[a.id] || 0));
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500" data-testid="admin-motm-status">
        {m.motm_voting_open ? `Vote ouvert · ${data.votes_total} vote(s)${m.motm_voting_closes_at ? ` · clôture auto ${new Date(m.motm_voting_closes_at).toLocaleString("fr-FR", { timeZone: "Indian/Mauritius" })}` : " · clôture dès qu'un prochain match existe"}`
          : m.motm_closed ? (m.motm_tie ? `Vote fermé · Égalité : ${data.motm_tied.map((t) => t.short_name).join(", ")} — à départager` : `Vote fermé · Homme(s) du Match : ${data.motm_winners.map((w) => w.short_name).join(", ") || "—"}`) : "Vote non ouvert"}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Btn data-testid="admin-motm-open" variant="secondary" disabled={m.motm_voting_open} onClick={() => act("post", "/motm/open", null, "Vote ouvert")}>Ouvrir le vote</Btn>
        <Btn data-testid="admin-motm-close" disabled={!m.motm_voting_open} onClick={() => act("post", "/motm/close", null, "Vote fermé")}>Fermer le vote</Btn>
      </div>
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {ranked.filter((p) => data.tally[p.id]).map((p) => (
          <div key={p.id} className="flex items-center gap-2 text-sm"><Avatar user={p} className="w-7 h-7 text-[9px]" /><span className="flex-1">{p.short_name}</span><span className="num font-bold">{data.tally[p.id]}</span></div>
        ))}
      </div>
      {data.admin_votes.length > 0 && (
        <div className="rounded-2xl bg-slate-50 p-3 text-xs space-y-0.5 max-h-48 overflow-y-auto" data-testid="admin-motm-votes">
          <p className="font-bold text-slate-500 uppercase mb-1">Qui a voté pour qui</p>
          {data.admin_votes.map((v, i) => <p key={i}>{v.voter} → <b>{v.voted}</b></p>)}
        </div>
      )}
      <Field label="Désigner le(s) Homme(s) du Match">
        <div className="flex flex-wrap gap-1.5">
          {players.map((p) => (
            <button key={p.id} data-testid={`admin-motm-pick-${p.id}`} onClick={() => setMulti(multi.includes(p.id) ? multi.filter((x) => x !== p.id) : [...multi, p.id])}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold border ${multi.includes(p.id) ? "bg-amber-400 border-amber-300" : "bg-white border-slate-200"}`}>{p.short_name}</button>
          ))}
        </div>
      </Field>
      <Btn data-testid="admin-motm-winner" className="w-full" disabled={!multi.length} onClick={() => act("put", "/motm/winner", { user_ids: multi }, "Homme(s) du Match enregistré(s)").then(() => setMulti([]))}>
        Valider ({multi.length})
      </Btn>
      <Link to={`/matchs/${id}`} className="block text-center text-xs text-cyan-600 underline">Corriger les buts / passes de ce match</Link>
    </div>
  );
};

export const MotmSection = () => {
  const [hist, setHist] = useState([]);
  const [id, setId] = useState("");
  useEffect(() => {
    api.get("/matches/history").then((r) => {
      const done = r.data.filter((h) => h.status === "completed");
      setHist(done);
      if (done[0]) setId(done[0].id);
    });
  }, []);
  return (
    <Card title="Homme du Match" testid="admin-motm-section">
      {!hist.length ? <p className="text-sm text-slate-500">Aucun match terminé.</p> : (
        <>
          <select data-testid="admin-motm-match-select" className="gg-input" value={id} onChange={(e) => setId(e.target.value)}>
            {hist.map((h) => <option key={h.id} value={h.id}>{fmtDate(h.date)} · {h.white_score}-{h.black_score}</option>)}
          </select>
          {id && <MotmPanel key={id} id={id} />}
        </>
      )}
    </Card>
  );
};

export const SettingsSection = () => {
  const [s, setS] = useState(null);
  const [pattern, setPattern] = useState("");
  useEffect(() => { api.get("/settings").then((r) => { setS(r.data); setPattern(r.data.draft_pattern.join("-")); }); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const body = { ...s, draft_pattern: pattern.split(/[^0-9]+/).map(Number).filter((n) => n > 0) };
      delete body.key; delete body.formation;
      const r = await api.put("/settings", body);
      setS(r.data);
      toast.success("Paramètres enregistrés (appliqués aux prochains matchs)");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const num = (k) => (e) => setS({ ...s, [k]: +e.target.value });
  const day = (k, label) => (
    <Field label={label}>
      <select data-testid={`settings-${k}`} className="gg-input" value={s[k]} onChange={num(k)}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select>
    </Field>
  );
  return (
    <Card title="Paramètres" testid="admin-settings-section">
      <div className="grid grid-cols-2 gap-2">
        {day("match_day", "Jour du match")}
        <Field label="Heure du match"><input data-testid="settings-time" type="time" className="gg-input" value={s.default_match_time} onChange={(e) => setS({ ...s, default_match_time: e.target.value })} /></Field>
        {day("registration_open_day", "Ouverture inscriptions")}
        <Field label="Heure d'ouverture"><input data-testid="settings-open-time" type="time" className="gg-input" value={s.registration_open_time} onChange={(e) => setS({ ...s, registration_open_time: e.target.value })} /></Field>
      </div>
      <Field label="Lieu par défaut"><input data-testid="settings-location" className="gg-input" value={s.default_location} onChange={(e) => setS({ ...s, default_location: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Capacité"><input data-testid="settings-capacity" type="number" className="gg-input" value={s.default_capacity} onChange={num("default_capacity")} /></Field>
        <Field label="Remplaçants"><input data-testid="settings-subs" type="number" className="gg-input" value={s.substitutes} onChange={num("substitutes")} /></Field>
        <Field label="Min. matchs (% V)"><input data-testid="settings-min-matches" type="number" className="gg-input" value={s.minimum_matches_win_rate} onChange={num("minimum_matches_win_rate")} /></Field>
        <Field label="Min. matchs capitaine"><input data-testid="settings-min-captain" type="number" className="gg-input" value={s.minimum_captain_matches_win_rate} onChange={num("minimum_captain_matches_win_rate")} /></Field>
      </div>
      <Field label="Séquence de draft par défaut"><input data-testid="settings-draft-pattern" className="gg-input" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="1 ou 2 ou 1-2-2-2-2-1" /></Field>
      <p className="text-xs text-slate-500">Fuseau horaire : Maurice (UTC+4) · Formation : 4-4-2</p>
      <Btn data-testid="settings-save" className="w-full" onClick={save}>Enregistrer</Btn>
    </Card>
  );
};

export const UsersSection = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await api.post("/admin/import-season-stats", { csv: await file.text() });
      toast.success(`${r.data.imported} ligne(s) de statistiques importée(s)`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title={`Joueurs · ${users.length}`} testid="admin-users-section">
      <label className="block rounded-2xl border-2 border-dashed border-cyan-300 bg-cyan-50 p-3 text-center text-sm font-bold text-cyan-700 cursor-pointer" data-testid="admin-import-stats">
        Importer les statistiques de saison (CSV)
        <span className="block text-[11px] font-normal text-slate-500 mt-0.5">Colonnes : account_id, full_name, email, season, matches, wins, draws, losses, points, goals, assists…</span>
        <input type="file" accept=".csv,text/csv" hidden onChange={importCsv} data-testid="admin-import-stats-input" />
      </label>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-2.5 rounded-2xl bg-slate-50 px-3 py-2">
            <Avatar user={u} className="w-8 h-8 text-[10px]" />
            <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{u.first_name} {u.last_name}</p><p className="text-[10px] text-slate-500">{u.role === "admin" ? "Administrateur" : "Joueur"}{u.account_claimed ? "" : " · compte non activé"}{u.account_id ? ` · #${u.account_id}` : ""}</p></div>
            {u.id !== user.id && (
              <>
                <button data-testid={`admin-user-role-${u.id}`} className="text-[11px] font-bold text-cyan-600" onClick={() => run(() => api.put(`/admin/users/${u.id}/role`, { role: u.role === "admin" ? "player" : "admin" }), "Rôle modifié")}>{u.role === "admin" ? "Retirer admin" : "Nommer admin"}</button>
                <button data-testid={`admin-user-delete-${u.id}`} className="text-[11px] font-bold text-red-500 ml-2" onClick={() => window.confirm(`Supprimer ${u.short_name} ?`) && run(() => api.delete(`/admin/users/${u.id}`), "Joueur supprimé")}>Supprimer</button>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
